import type { AppRouteRecordRaw, Menu } from '/@/router/types';

import { defineStore } from 'pinia';
import { store } from '/@/store';
import { useI18n } from '/@/hooks/web/useI18n';
import { useUserStore } from './user';
import { useAppStoreWithOut } from './app';
import { toRaw } from 'vue';
import {
  transformObjToRoute,
  flatMultiLevelRoutes,
  addSlashToRouteComponent,
} from '/@/router/helper/routeHelper';
import { transformRouteToMenu } from '/@/router/helper/menuHelper';

import projectSetting from '/@/settings/projectSetting';

import { PermissionModeEnum } from '/@/enums/appEnum';

import { asyncRoutes } from '/@/router/routes';
import { ERROR_LOG_ROUTE, PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';

import { filter } from '/@/utils/helper/treeHelper';

import { getMenuList } from '/@/api/sys/menu';
import { getPermCode } from '/@/api/sys/user';

import { useMessage } from '/@/hooks/web/useMessage';
import { PageEnum } from '/@/enums/pageEnum';
import WebStorage from '/@/utils/cache';
import { APP_CODE, TENANT_ID } from '/@/enums/cacheEnum';
import { HandlerEnum, menuTypeList } from '/@/layouts/default/setting/enum';
import { baseHandler } from '/@/layouts/default/setting/handler';
import { useMenuSetting } from '/@/hooks/setting/useMenuSetting';
import { useEnterpriseSpace } from '/@/views/modules/shanshe/enterprise_space/useEnterpriseSpace';
import { EN_ROLE_LEVEL } from '/@/views/modules/shanshe/enums/roleLevelEnum';
import { initStandardBranchManager, loadPlugins } from '/@/standard_branch';

// 系统权限
interface AuthItem {
  // 菜单权限编码，例如：“sys:schedule:list,sys:schedule:info”,多个逗号隔开
  action: string;
  // 权限策略1显示2禁用
  type: string | number;
  // 权限状态(0无效1有效)
  status: string | number;
  // 权限名称
  describe?: string;
  isAuth?: boolean;
}

// app
export interface AppItem {
  // id
  id: string;
  // name
  name: string;
  // appCode
  appCode: string;
  // app所在租户id
  tenantId: string;
  // 跳转路径
  url: string;
  // 是否展开菜单
  expandMenu?: string;
  // 头部主题颜色
  headerThemeColor?: string;
  // 是否是当前选中的
  isDefault?: boolean;
  // logo
  logo: string;
  // 菜单主题颜色
  menuThemeColor?: string;
  // 布局模式
  navMode?: string;
  // 系统主题颜色
  systemThemeColor?: string;
  // 菜单伸缩按钮
  menuCollapseButton?: string;

  isSelected?: boolean;

  link: string;
}

// 租户
export interface TenantsItem {
  // 租户id
  id: string;
  // 租户name
  name?: string;
  // 租户授权app
  apps?: AppItem[];
}

interface PermissionState {
  // Permission code list
  permCodeList: string[] | number[];
  // Whether the route has been dynamically added
  isDynamicAddedRoute: boolean;
  // To trigger a menu update
  lastBuildMenuTime: number;
  // Backstage menu list
  backMenuList: Menu[];
  menuList: Menu[];
  tenants: TenantsItem[];
  internalTenants: TenantsItem[];
  apps: AppItem[];
  frontMenuList: Menu[];
  // 用户所拥有的权限
  authList: AuthItem[];
  // 全部权限配置
  allAuthList: AuthItem[];
  // 系统安全模式
  sysSafeMode: boolean;
  // 业务权限(新接口)
  businessPermission: string[];
}
export const usePermissionStore = defineStore({
  id: 'app-permission',
  state: (): PermissionState => ({
    permCodeList: [],
    // Whether the route has been dynamically added
    isDynamicAddedRoute: false,
    // To trigger a menu update
    lastBuildMenuTime: 0,
    // Backstage menu list
    backMenuList: [],
    menuList: [],
    // menu List
    frontMenuList: [],
    // tenant List
    tenants: [],
    internalTenants: [], // 内部权限的租户
    // 个人app List
    apps: [],
    authList: [],
    allAuthList: [],
    sysSafeMode: false,
    businessPermission: [],
  }),
  getters: {
    getBusinessPermission(): string[] {
      return this.businessPermission;
    },
    getPermCodeList(): string[] | number[] {
      return this.permCodeList;
    },
    getBackMenuList(): Menu[] {
      return this.backMenuList;
    },
    getMenuList(): Menu[] {
      return this.menuList;
    },
    getTenantsList(): TenantsItem[] {
      return this.tenants;
    },
    getInternalTenantsList(): TenantsItem[] {
      return this.internalTenants;
    },
    getAppsList(): AppItem[] {
      return this.apps;
    },
    getFrontMenuList(): Menu[] {
      return this.frontMenuList;
    },
    getLastBuildMenuTime(): number {
      return this.lastBuildMenuTime;
    },
    getIsDynamicAddedRoute(): boolean {
      return this.isDynamicAddedRoute;
    },
  },
  actions: {
    setBusinessPermission(businessPermission: string[]) {
      this.businessPermission = businessPermission;
    },
    setPermCodeList(codeList: string[]) {
      this.permCodeList = codeList;
    },

    setBackMenuList(list: Menu[]) {
      this.backMenuList = list;
      list?.length > 0 && this.setLastBuildMenuTime();
    },

    setMenuList(list: Menu[]) {
      this.menuList = list;
    },

    setTenantsList(list: TenantsItem[]) {
      this.tenants = list;
    },
    setIntenalTenantsList(list: TenantsItem[]) {
      this.internalTenants = list;
    },
    setAppsList(list: AppItem[]) {
      this.apps = list;
    },

    setFrontMenuList(list: Menu[]) {
      this.frontMenuList = list;
    },

    setLastBuildMenuTime() {
      this.lastBuildMenuTime = new Date().getTime();
    },

    setDynamicAddedRoute(added: boolean) {
      this.isDynamicAddedRoute = added;
    },
    resetState(): void {
      this.isDynamicAddedRoute = false;
      this.permCodeList = [];
      this.backMenuList = [];
      this.lastBuildMenuTime = 0;
    },
    async changePermissionCode() {
      const systemPermission = await getPermCode();
      const codeList = systemPermission.codeList;
      this.setPermCodeList(codeList);
      this.setAuthData(systemPermission);
    },
    async buildRoutesAction(): Promise<AppRouteRecordRaw[]> {
      const { t } = useI18n();
      const userStore = useUserStore();
      const appStore = useAppStoreWithOut();

      let routes: AppRouteRecordRaw[] = [];
      const roleList = toRaw(userStore.getRoleList) || [];
      const { permissionMode = projectSetting.permissionMode } = appStore.getProjectConfig;

      const routeFilter = (route: AppRouteRecordRaw) => {
        const { meta } = route;
        const { roles } = meta || {};
        if (!roles) return true;
        return roleList.some((role) => roles.includes(role));
      };

      const routeRemoveIgnoreFilter = (route: AppRouteRecordRaw) => {
        const { meta } = route;
        const { ignoreRoute } = meta || {};
        return !ignoreRoute;
      };

      /**
       * @description 根据设置的首页path，修正routes中的affix标记（固定首页）
       * */
      const patchHomeAffix = (routes: AppRouteRecordRaw[]) => {
        if (!routes || routes.length === 0) return;
        let homePath: string = userStore.getUserInfo.homePath || PageEnum.BASE_HOME;
        function patcher(routes: AppRouteRecordRaw[], parentPath = '') {
          if (parentPath) parentPath = parentPath + '/';
          routes.forEach((route: AppRouteRecordRaw) => {
            const { path, children, redirect } = route;
            const currentPath = path.startsWith('/') ? path : parentPath + path;
            if (currentPath === homePath) {
              if (redirect) {
                homePath = route.redirect! as string;
              } else {
                route.meta = Object.assign({}, route.meta, { affix: true });
                throw new Error('end');
              }
            }
            children && children.length > 0 && patcher(children, currentPath);
          });
        }
        try {
          patcher(routes);
        } catch (e) {
          // 已处理完毕跳出循环
        }
        return;
      };

      switch (permissionMode) {
        case PermissionModeEnum.ROLE:
          routes = filter(asyncRoutes, routeFilter);
          routes = routes.filter(routeFilter);
          //  将多级路由转换为二级
          routes = flatMultiLevelRoutes(routes);
          break;

        case PermissionModeEnum.ROUTE_MAPPING:
          routes = filter(asyncRoutes, routeFilter);
          routes = routes.filter(routeFilter);
          const menuList = transformRouteToMenu(routes, true);
          routes = filter(routes, routeRemoveIgnoreFilter);
          routes = routes.filter(routeRemoveIgnoreFilter);
          menuList.sort((a, b) => {
            return (a.meta?.orderNo || 0) - (b.meta?.orderNo || 0);
          });
          this.setFrontMenuList(menuList);
          // 将多级路由转换为二级
          routes = flatMultiLevelRoutes(routes);
          break;

        // 后台菜单构建
        case PermissionModeEnum.BACK:
          const { createMessage, createWarningModal } = useMessage();
          // 菜单加载提示
          // createMessage.loading({
          //   content: t('sys.app.menuLoading'),
          //   duration: 1,
          // });

          // 从后台获取权限码，
          // 这个函数可能只需要执行一次，并且实际的项目可以在正确的时间被放置
          let routeList: AppRouteRecordRaw[] = [];
          let tenantList: TenantsItem[] = [];
          let tenantInternalList: TenantsItem[] = [];
          let appList: AppItem[] = [];
          try {
            this.changePermissionCode();
            const { menu, tenants, apps, tenantInternal, currentApp } = (await getMenuList()) as {
              menu: AppRouteRecordRaw[];
              tenants: TenantsItem[];
              tenantInternal: TenantsItem[];
              apps: AppItem[];
              currentApp: string;
            };
            if (currentApp && currentApp !== WebStorage.get(APP_CODE)) {
              WebStorage.set(APP_CODE, currentApp);
            }
            const { createEnterpriseSpace, enterpriseInfo } = useEnterpriseSpace();
            await createEnterpriseSpace();
            // 为企业空间hardcode

            if (
              (enterpriseInfo.value?.permission !== null &&
                enterpriseInfo.value?.permission.level) ??
              -1 < EN_ROLE_LEVEL.LOOK
            ) {
              const enterprise_space = menu.find((m) => m.path.indexOf('enterprise-space') !== -1);
              if (enterprise_space) {
                menu.splice(menu.indexOf(enterprise_space), 1);
              }
            }

            this.setMenuList(JSON.parse(JSON.stringify(menu)));
            routeList = menu as AppRouteRecordRaw[];

            // update-begin----author:sunjianlei---date:20220315------for: 判断是否是 vue3 版本的菜单 ---
            let hasIndex = false;
            let hasIcon = false;
            for (const menuItem of routeList) {
              // 条件1：判断组件是否是 layouts/default/index
              if (!hasIndex) {
                hasIndex = menuItem.component === 'layouts/default/index';
              }
              // 条件2：判断图标是否带有 冒号
              if (!hasIcon) {
                hasIcon = !!menuItem.meta?.icon?.includes(':');
              }
              // 满足任何一个条件都直接跳出循环
              if (hasIcon || hasIndex) {
                break;
              }
            }
            tenantList = tenants;
            tenantInternalList = tenantInternal;
            appList = apps;
            // 两个条件都不满足，就弹出提示框
            if (!hasIcon && !hasIndex) {
              console.warn('需要记得处理vue3菜单问题');
              // 延迟1.5秒之后再出现提示，否则提示框出不来
              // setTimeout(
              //     () =>
              //         createWarningModal({
              //             title: '提示',
              //             content:
              //                 '检测到你可能使用了<b>非vue3版本</b>的数据库表，这将会导致菜单或其他功能出现异常，请更换成vue3版本的数据库表后刷新。' +
              //                 '<br>文档地址：<a href="http://vue3.jeecg.com/2671576" target="_blank">http://vue3.jeecg.com/2671576</a>',
              //         }),
              //     1500
              // );
            }
            // update-end----author:sunjianlei---date:20220315------for: 判断是否是 vue3 版本的菜单 ---
          } catch (error) {
            console.error(error);
          }
          // 组件地址前加斜杠处理  author: lsq date:2021-09-08
          routeList = addSlashToRouteComponent(routeList);
          // 动态引入组件
          routeList = transformObjToRoute(routeList);

          // 构建后台路由菜单
          const backMenuList = transformRouteToMenu(routeList);
          this.setBackMenuList(backMenuList);
          this.setTenantsList(tenantList);
          this.setIntenalTenantsList(tenantInternalList);
          this.setAppsList(appList);

          // 删除meta.ignoreRoute项
          routeList = filter(routeList, routeRemoveIgnoreFilter);
          routeList = routeList.filter(routeRemoveIgnoreFilter);

          // routeList = flatMultiLevelRoutes(routeList);
          routes = [PAGE_NOT_FOUND_ROUTE, ...routeList];
          break;
      }
      routes.push(ERROR_LOG_ROUTE);
      patchHomeAffix(routes);

      const tenantId = WebStorage.get(TENANT_ID);
      const tenant = [...this.tenants, ...this.internalTenants].find((t) => t.id === tenantId);
      initStandardBranchManager({
        tenantName: tenant?.name ?? '',
        spaceRelTenantId: tenant?.id ?? '',
      });
      await loadPlugins();

      return routes;
    },
    setAuthData(systemPermission) {
      this.authList = systemPermission.auth;
      this.allAuthList = systemPermission.allAuth;
      this.sysSafeMode = systemPermission.sysSafeMode;
    },
    setAuthList(authList: AuthItem[]) {
      this.authList = authList;
    },
    setAllAuthList(authList: AuthItem[]) {
      this.allAuthList = authList;
    },
  },
});

// 需要在设置之外使用
export function usePermissionStoreWithOut() {
  return usePermissionStore(store);
}
